import {useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {alpha, Box, Divider, Tooltip} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import AppsOutlinedIcon from '@mui/icons-material/AppsOutlined';
import {useAppTranslation} from "@mybake/ui/hooks/useAppTranslation";
import {getActiveNavigationPath, navigationItems} from '../navBar/navigationConfig';

type Props = {
    logout: () => void;
    canOpenAdmin?: boolean;
    isAdmin?: boolean;
};

export default function LongMenu({logout, canOpenAdmin = false, isAdmin = false}: Props) {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const open = Boolean(anchorEl);
    const {t} = useAppTranslation();

    const history = useNavigate();
    const location = useLocation();
    const activePath = getActiveNavigationPath(location.pathname);
    const handleLocationChange = (newValue: string) => {
        history(newValue);
    };

    // methods
    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };
    const handleMenuItem = (path: string) => {
        if (path === '/logout') logout();
        else handleLocationChange(path);
        handleClose();
    };

    // styles
    const menuWrapper = {display: 'flex', alignItems: 'center'};
    const menuListProps = {
        'aria-labelledby': 'long-button',
    };
    const PaperPropsStyle = {
        sx: {
            width: 'min(300px, 85vw)',
            mt: 0.75,
            borderRadius: 2,
            bgcolor: 'background.paper',
            color: 'text.primary',
            boxShadow: '0 12px 36px rgba(28, 28, 28, 0.18)',
            backgroundImage: 'none',
        },
    };

    return (
        <Box style={menuWrapper}>
            <IconButton
                aria-label={t('common:more_actions')}
                id="long-button"
                aria-controls={open ? 'long-menu' : undefined}
                aria-expanded={open ? 'true' : undefined}
                aria-haspopup="true"
                onClick={handleClick}
                sx={{display: {xs: 'inline-flex', md: 'none'}, p: 1}}
            >
                <MenuIcon/>
            </IconButton>
            <Tooltip title={t('navigation:logout')}>
                <IconButton aria-label={t('navigation:logout')} onClick={logout} sx={{display: {xs: 'none', md: 'inline-flex'}}}>
                    <LogoutIcon/>
                </IconButton>
            </Tooltip>
            <Menu
                id="long-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                disableScrollLock
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                BackdropProps={{
                    sx: {
                        backgroundColor: 'rgba(28, 28, 28, 0.12)',
                        backdropFilter: 'blur(4px)',
                        WebkitBackdropFilter: 'blur(4px)',
                    },
                }}
                MenuListProps={menuListProps}
                PaperProps={PaperPropsStyle}
            >
                {navigationItems.map((item) => (
                    <MenuItem
                        key={item.path}
                        selected={activePath === item.path}
                        onClick={() => handleMenuItem(item.path)}
                        sx={(theme) => ({minHeight: 48, px: 2.5, fontWeight: 600, color: activePath === item.path ? 'primary.main' : 'text.primary', '&.Mui-selected': {bgcolor: alpha(theme.palette.primary.main, 0.1)}, '&.Mui-selected:hover, &:hover': {bgcolor: alpha(theme.palette.primary.main, 0.14)}})}
                    >
                        {t(item.translationKey)}
                    </MenuItem>
                ))}
                {canOpenAdmin && <MenuItem onClick={() => handleMenuItem(isAdmin ? '/app' : '/admin')} sx={{display: {md: 'none'}, minHeight: 48, px: 2.5, fontWeight: 600}}>{isAdmin ? <AppsOutlinedIcon sx={{mr: 1.5}}/> : <AdminPanelSettingsOutlinedIcon sx={{mr: 1.5}}/>}{isAdmin ? t('auth:return_to_app') : t('admin:dashboard.title' as never)}</MenuItem>}
                <Divider sx={{my: 0.75}}/>
                <MenuItem onClick={() => handleMenuItem('/logout')} sx={{minHeight: 48, px: 2.5, fontWeight: 600, color: 'text.primary'}}>
                    {t('navigation:logout')}
                </MenuItem>
            </Menu>
        </Box>
    );
}
